import { ReloadOutlined } from "@ant-design/icons";
import {
  type Cancellation,
  staticError,
  useGuardedEffect,
} from "@p0security/frontend-core";
import { MaturityTag } from "@p0security/frontend-core/react";
import { visibleOptionEntries } from "@p0security/shared/install/options";
import type {
  ElementInstaller,
  Option,
  OptionsElement,
} from "@p0security/shared/install/types";
import type { IntegrationStatus } from "@p0security/shared/integrations/shared";
import type { BaseOptionType } from "@p0security/ui";
import {
  Button,
  type DefaultOptionType,
  Flex,
  Form,
  Input,
  Select,
  Spacing,
  Spin,
  Switch,
  Typography,
} from "@p0security/ui";
import classNames from "classnames";
import { capitalize, cloneDeep, isEmpty, pick, set } from "lodash";
import type { ChangeEvent, FocusEvent, MouseEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  FrontendComponentInstaller,
  FrontendInstallContext,
  FrontendInstallDomain,
} from "../types";
import { NEW_SENTINEL } from "./Component";

// TODO: Type properly
type FormProps = {
  /** The item or field being configured */
  config: any;
  context: FrontendInstallContext<any>;
  /** If true, this and all child elements will be disabled */
  allDisabled?: boolean;
  /** The ID of the item being configured */
  id: string;
  /** This field's frontend installer */
  installer?: FrontendComponentInstaller<any>;
  /** True to indicate that an API request is underway */
  isFetching: boolean;
  /** The base item being configured */
  item: any;
  /** Callback to refresh the data used by a this form field */
  refresh?: ((value: any) => void) | undefined;
  /** This field's component schema */
  schema: Record<string, any>;
  /** Callback to execute to update this field's value */
  setConfig: ((value: any) => void) | undefined;
  /** The item's current installation phase:
   *
   * - configure: Item is already installed in customer environment, user is configuring the item
   * - new: Item is not yet created, user is starting installation
   */
  step: "configure" | "new";
};

type HelpProps = Pick<FormProps, "config" | "context" | "id"> & {
  element?: any;
  installer?: ElementInstaller<FrontendInstallDomain<any>, any, any, any>;
  item: any;
};

type StringElementProps = Omit<ItemFormProps, "configKey">;

type SwitchElementProps = Omit<ItemFormProps, "configKey" | "element">;

type DynamicElementProps = Omit<ItemFormProps, "configKey">;

type GeneratedElementProps = Omit<
  ItemFormProps,
  "configKey" | "element" | "setConfig"
>;

type EncryptedElementMode = "hash" | "input";

type EncryptedElementProps = Omit<ItemFormProps, "configKey"> & {
  state: IntegrationStatus;
};

type OptionElementProps = Omit<
  ItemFormProps,
  "element" | "installer" | "schema"
> & {
  element: OptionsElement<Record<string, Option<any>>>;
  installer?: ElementInstaller<FrontendInstallDomain<any>, any, any, any>;
};

type ComponentFormProps = FormProps & {
  /** Location of `config` within `item`; omit for the root config */
  path?: string[];
};

type ItemFormProps = Omit<FormProps, "installer" | "schema"> & {
  configKey: string;
  element: any;
  /** True if this element only is disabled (inner elements may still be enabled) */
  disabled: boolean;
  installer?: ElementInstaller<FrontendInstallDomain<any>, any, any, any>;
  path: string[];
};

// Shared so the root path is referentially stable across renders
const ROOT_PATH: string[] = [];

/** One configuration element and the layout of the controls antd renders
 * inside it. A hidden element is laid out as `display: none` rather than
 * dropped, so it still submits its default value. */
const OPTION_CLASSES =
  "min-h-8 max-w-[38em] flex-col items-start gap-[0.5em] [&_.ant-form-item]:mb-[5px] [&_.ant-form-item_.ant-input:not(:last-child)]:mb-[5px] [&_.ant-form-item_.ant-select:last-child]:mb-[15px] [&_.p0-description]:mb-2 [&_.p0-description]:w-[35em] [&_.p0-description_.ant-typography_code]:whitespace-nowrap [&_.p0-string-element]:w-[35em] [&_.p0-string-element]:gap-[0.2em] [&_.p0-switch-element]:mb-[15px]";

const useComponentPrerequisites = (
  props: Pick<ItemFormProps, "context" | "id" | "installer" | "item">
) => {
  const { context, id, item, installer } = props;
  const [messages, setMessages] = useState<string>();

  useGuardedEffect(
    (cancellation) => async () => {
      if (!installer || !("prerequisiteMessages" in installer)) return;
      const messages = await installer.prerequisiteMessages?.(
        context,
        id,
        item
      );
      cancellation.guard(setMessages)(messages);
    },
    [context, id, installer, item],
    staticError
  );

  return { messages };
};

const Description = (props: HelpProps) => {
  const { config, context, element, id, installer, item } = props;
  const description = useMemo(
    () =>
      installer?.labeler?.(context, id, item, config) ?? element.description,
    [installer, context, id, item, config, element.description]
  );

  return description ? (
    <div className="p0-description">
      <Typography.Text type="secondary">{description}</Typography.Text>
    </div>
  ) : null;
};

const StringElement = (props: StringElementProps) => {
  const { config, setConfig, element, disabled } = props;
  const { messages } = useComponentPrerequisites(props);
  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setConfig?.(event.target.value);
    },
    [setConfig]
  );
  const onBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const trimmed = event.target.value.trim();
      if (trimmed !== event.target.value) setConfig?.(trimmed);
    },
    [setConfig]
  );

  const InputElement = element.multiline ? Input.TextArea : Input;
  return (
    <Flex className="p0-string-element" gap="0.7em" vertical>
      {messages}
      <InputElement
        disabled={disabled || !!messages}
        onBlur={onBlur}
        onChange={onChange}
        placeholder={element.placeholder}
        value={config}
      />
    </Flex>
  );
};

/**
 *  EncryptedElement is a component that allows the user to view and edit an encrypted string.
 * The user can switch between view and edit mode using a switch.
 * when in view mode, the user can see the encrypted string, and when in edit mode,the prefilled value is cleared.
 * @param props
 * @returns
 */
const EncryptedElement = (props: EncryptedElementProps) => {
  const { config, setConfig, state, disabled } = props;
  const [mode, setMode] = useState<EncryptedElementMode>(
    state === "installed" && config?.hash ? "hash" : "input"
  );
  const [value, setValue] = useState<string | undefined>(config?.hash);
  const [oldValue] = useState<string | undefined>(config?.hash);
  const [newValue, setNewValue] = useState<string | undefined>(
    config?.clearText
  );

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // empty values should be considered as undefined
      setConfig?.({
        hash: oldValue,
        clearText: event.target.value !== "" ? event.target.value : undefined,
      });
      setValue(event.target.value);
      setNewValue(event.target.value);
    },
    [setConfig, setValue, setNewValue, oldValue]
  );
  // Reset the value when switching between view and edit mode
  // any change in the switch clears the value
  const changeMode = useCallback(() => {
    if (mode === "input") {
      setMode("hash");
      setValue(oldValue);
      setConfig?.({ hash: oldValue });
    } else {
      setMode("input");
      setValue(newValue);
      if (newValue !== "") setConfig?.({ ...config, clearText: newValue });
    }
  }, [mode, config, setMode, setConfig, oldValue, newValue]);

  const Element = mode === "hash" ? Input : Input.Password;

  return (
    <Flex className="p0-string-element" gap="0.7em" vertical>
      <Flex gap={12} align="baseline">
        <Element
          value={value}
          onChange={onChange}
          disabled={disabled || mode === "hash"}
          {...(mode === "hash" ? {} : {})}
        />
        {state === "installed" && (
          <Flex className="flex-nowrap" gap={4}>
            <Flex className="w-fit" vertical>
              {`${capitalize(mode)}`}&nbsp;
            </Flex>
            <Switch
              onChange={changeMode}
              /* eslint-disable-line react/forbid-component-props */ style={
                newValue ? { backgroundColor: "orange" } : undefined
              }
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

/** A dynamic element's option; `label` may render an icon, so search
 * matches `searchText` instead */
type DynamicSelectOption = BaseOptionType & { searchText: string };

const IconLabel = ({
  iconUrl,
  label,
}: {
  iconUrl: string;
  label: DynamicSelectOption["label"];
}) => (
  <Flex align="center" gap={8}>
    <img
      alt=""
      className="shrink-0 object-contain"
      height={20}
      src={iconUrl}
      width={20}
    />
    {label}
  </Flex>
);

const DynamicElement = (props: DynamicElementProps) => {
  const { config, context, disabled, id, installer, item, setConfig } = props;
  const { messages } = useComponentPrerequisites(props);
  const [options, setOptions] = useState<DynamicSelectOption[]>();

  // Hack to prevent auto-close of antd select after click
  const onClickSelect = useCallback((event: MouseEvent) => {
    event.preventDefault();
  }, []);

  const onSelect = useCallback(
    (value: string) => {
      setConfig?.(value);
    },
    [setConfig]
  );

  const refreshOptions = useCallback(
    async (cancellation?: Cancellation) => {
      if (installer && "optionProvider" in installer) {
        try {
          const items = await installer.optionProvider(context, item, id);
          if (cancellation?.isCancelled) return;
          const options = items.map(({ iconUrl, id, label = id }) => ({
            label: iconUrl ? (
              <IconLabel iconUrl={iconUrl} label={label} />
            ) : (
              label
            ),
            // Only a string label is searchable text; fall back to the id
            searchText: typeof label === "string" ? label : id,
            value: id,
          }));
          setOptions(options);
        } catch (error) {
          // Present an empty list rather than loading indefinitely; this
          // element only leaves its loading state once options are set
          staticError(
            `Could not load options for ${id}: ${error instanceof Error ? error.message : String(error)}`
          );
          setOptions([]);
        }
      } else setOptions([]);
    },
    [item, context, id, installer]
  );

  useGuardedEffect(
    (cancellation) => async () => refreshOptions(cancellation),
    [context, installer, refreshOptions],
    staticError
  );

  useEffect(() => {
    if (!config && options?.[0]) setConfig?.(options?.[0]?.value);
  }, [config, options, setConfig]);

  // filter should be done based on description as
  // default search behavior searches for ids only
  const filterOption = useCallback(
    (input: string, option?: DynamicSelectOption) =>
      (option?.searchText ?? "").toLowerCase().includes(input.toLowerCase()),
    []
  );
  const reload = useMemo(() => {
    if (installer && "refresh" in installer) {
      return () => {
        void installer.refresh?.(props.context, props.id);
        void refreshOptions();
      };
    }
  }, [installer, props.context, props.id, refreshOptions]);

  return (
    <>
      {messages}
      {options ? (
        <Flex gap="0.5em">
          <Select
            disabled={disabled || !!messages}
            filterOption={filterOption}
            onChange={onSelect}
            onClick={onClickSelect}
            options={options}
            showSearch
            value={config}
          />
          {!disabled && reload && (
            <Form.Item key="refresh">
              <Button type="default" onClick={reload}>
                <ReloadOutlined />
              </Button>
            </Form.Item>
          )}
        </Flex>
      ) : (
        <Spin />
      )}
    </>
  );
};

const GeneratedElement = ({ config }: GeneratedElementProps) => (
  <Flex className="p0-string-element" gap="0.7em" vertical>
    <Input value={config} disabled />
  </Flex>
);

const SwitchElement = (props: SwitchElementProps) => {
  const { config, setConfig, disabled } = props;
  const onChange = useCallback(
    (checked: boolean) => {
      setConfig?.(checked);
    },
    [setConfig]
  );

  useEffect(() => {
    if (config === undefined) setConfig?.(false);
  }, [config, setConfig]);

  return (
    <div className="p0-switch-element">
      <Switch checked={config} disabled={disabled} onChange={onChange} />
    </div>
  );
};

const renderOptionItem = ({
  data,
}: {
  data: { label?: ReactNode; description: ReactNode; prerequisites?: string };
}) => (
  <Flex vertical>
    {data.label}
    {data.description ? (
      <div className="text-wrap">{data.description}</div>
    ) : null}
    {data.prerequisites ? (
      <div>
        <Typography.Text type="warning">{data.prerequisites}</Typography.Text>
      </div>
    ) : null}
  </Flex>
);

/** Renders a selection for a statically defined component option
 *
 * Available options are rendered in a dropdown. Configuration options are
 * rendered below the dropdown.
 *
 * If only one option is available, the dropdown is disabled.
 *
 * If the config type is not a valid option, it is automatically reset to
 * the default option (if that is valid), or the first available option.
 */
const OptionElement = (props: OptionElementProps) => {
  const { config, context, disabled, element, id, item, installer } = props;
  const { path, setConfig } = props;

  const [installers, setInstallers] = useState<
    Record<string, { prerequisites?: ReactNode }>
  >({});
  const [loading, setLoading] = useState(true);

  const setOption = useCallback(
    (type: string) => {
      // Carry existing values over only when not switching away from another
      // option; options may share key names (e.g. aws / gcp federation
      // `provider`), which would otherwise leak stale values
      const carriedKeys =
        !config?.type || config.type === type
          ? Object.keys(element.options[type].schema)
          : [];
      setConfig?.({ ...pick(config, carriedKeys), type });
    },
    [config, element.options, setConfig]
  );

  // Prevent rendering loop by assigning console.error to a static value
  const logError = useCallback((message: string) => console.error(message), []);

  // Determines the available options and their installers
  useGuardedEffect(
    (cancellation) => async () => {
      const optionInstaller =
        installer && "options" in installer ? installer : undefined;
      const allowed = await optionInstaller?.optionsProvider?.(
        context,
        set(cloneDeep(item), path, config),
        id
      );
      const promises = Object.keys(element.options)
        // `allowed` is size < 10, so use includes instead of creating set
        .filter((key) => !allowed || allowed.includes(key))
        .map(async (key) => {
          const keyInstaller = optionInstaller?.options[key];
          const prerequisites = await keyInstaller?.prerequisiteMessages?.(
            context,
            id,
            item
          );
          return [key, { prerequisites }];
        });
      const results = await Promise.all(promises);
      if (cancellation.isCancelled) return;
      setInstallers(Object.fromEntries(results));
      setLoading(false);
    },
    [context, element, config, id, installer, item, path],
    logError
  );

  // Selectable items
  const items = useMemo(
    () =>
      visibleOptionEntries(element.options, context.environmentType)
        .filter(([key]) => key in installers)
        .map<DefaultOptionType & { description: ReactNode }>(
          ([key, option]) => {
            const prerequisites = installers[key]?.prerequisites;
            return {
              description: option.description ? (
                <Typography.Text type="secondary">
                  {option.description}
                </Typography.Text>
              ) : null,
              prerequisites,
              disabled: !!prerequisites || disabled,
              value: key,
              label: (
                <Flex gap={4} align="baseline" justify="space-between">
                  {option.label}
                  {option.maturity !== "ga" && (
                    <MaturityTag maturity={option.maturity} />
                  )}
                </Flex>
              ),
            };
          }
        ),
    [context.environmentType, disabled, element.options, installers]
  );

  // The selected value, chosen in order:
  // - The config type, if valid
  // - The element default, if valid
  // - The first available valid option
  const value = useMemo(
    () =>
      config?.type && config.type in installers
        ? config.type
        : element.default in installers
          ? element.default
          : items?.[0]?.value,
    [config?.type, element.default, installers, items]
  );

  // If the saved value is invalid, reset it to the selected value
  useEffect(() => {
    if (value && (!config?.type || !(config.type in installers)))
      setOption(value);
  }, [config?.type, element.options, installers, setOption, value]);

  return loading ? (
    <Spin />
  ) : installers ? (
    <Flex vertical gap={8}>
      <Select<
        string,
        DefaultOptionType & { description: ReactNode; prerequisites?: string }
      >
        disabled={disabled || items.length < 2}
        onChange={setOption}
        options={items}
        optionRender={renderOptionItem}
        value={value}
      />
      {config?.type && config.type in installers ? (
        <div className="pl-4!" key={config.type}>
          <ComponentForm
            {...props}
            installer={
              installer && "options" in installer
                ? (installer.options[config.type] as any)
                : undefined
            }
            schema={element.options[config.type].schema}
          />
        </div>
      ) : null}
    </Flex>
  ) : (
    <Spin />
  );
};

const ItemForm = (props: ItemFormProps) => {
  const { config, configKey: key, context } = props;
  const { element, id, installer, setConfig } = props;

  // This element is disabled if:
  // - the parent element is disabled for all children (props.disabled)
  // - the element is only configurable on "new", and we're not on that step
  const disabled =
    props.allDisabled || (element.step === "new" && props.step !== "new");

  const path = useMemo(() => [...props.path, key], [key, props.path]);

  const [error, setError] = useState<ReactNode>();
  const setItem = useCallback(
    async (value: any) => {
      setConfig?.({ ...config, [key]: value });
      let elementMessage: string | undefined;
      try {
        elementMessage = await element?.validator?.(
          id === NEW_SENTINEL ? value : id,
          value
        );
      } catch (error) {
        elementMessage = error instanceof Error ? error.message : String(error);
      }

      const getErrorDisplay = () => {
        if (!elementMessage) return undefined;
        if (installer && installer.errorElement) {
          return installer.errorElement(context, id);
        }
        return elementMessage;
      };

      setError(getErrorDisplay());
    },
    [config, context, element, id, installer, key, setConfig]
  );
  const subProps = {
    ...props,
    config: config?.[key],
    disabled,
    path,
    setConfig: setItem,
  };

  return element.type === "hidden" ? null : (
    <div
      key={key}
      // Do not omit the element, we want invisible elements that will still
      // submit their default values to the backend. The backend relies on
      // receiving all fields, even if they are hidden.
      className={classNames(
        OPTION_CLASSES,
        (installer?.hidden?.(context, id) ?? false) ? "hidden" : "flex"
      )}
    >
      <Form.Item
        className="w-full"
        hasFeedback
        help={error}
        label={element.label}
        required={!element.optional}
        validateStatus={error ? "error" : undefined}
      >
        <Description {...props} />
        {element.type === "select" ? (
          <OptionElement {...subProps} />
        ) : element.type === "string" ? (
          <StringElement {...subProps} />
        ) : element.type === "encrypted" ? (
          <EncryptedElement {...subProps} state={config.state} />
        ) : element.type === "dynamic" ? (
          <DynamicElement {...subProps} />
        ) : element.type === "generated" ? (
          <GeneratedElement {...subProps} />
        ) : element.type === "switch" ? (
          <SwitchElement {...subProps} />
        ) : null}
      </Form.Item>
    </div>
  );
};

const ComponentForm = (props: ComponentFormProps) => {
  const { installer, schema, config, item } = props;
  const path = props.path ?? ROOT_PATH;
  return (
    <>
      {Object.entries(schema).map(([key, element]) => (
        <ItemForm
          {...props}
          config={config}
          configKey={key}
          disabled={!!props.allDisabled}
          element={element}
          installer={
            installer && "items" in installer ? installer.items[key] : {}
          }
          item={item}
          key={key}
          path={path}
        />
      ))}
    </>
  );
};

const ConfigFormFooter = (props: ComponentFormProps) => {
  const { installer, context, id, config } = props;

  const footer = useMemo(
    () => installer?.footer?.(context, id, config),
    [config, context, id, installer]
  );

  return footer as ReactNode;
};

const ConfigFormSubmitActions = (props: ComponentFormProps) => {
  const { installer, context, id, config } = props;

  const submitActions = useMemo(
    () => installer?.submitActions?.(context, id, config),
    [config, context, id, installer]
  );

  return submitActions as ReactNode;
};

const SubmitButton = ({
  config,
  disabled,
  onClick,
}: {
  config: ComponentFormProps["config"];
  disabled: boolean;
  onClick: () => void;
}) => (
  <Button
    type="primary"
    onClick={onClick}
    // TODO: make work with initial defaults
    disabled={disabled}
  >
    {config.state === "configure"
      ? "Finish"
      : config.state === "installed"
        ? "Update"
        : "Next"}
  </Button>
);

/** Recursively render the configuration UI for an install component. */
export const ConfigForm = (props: FormProps) => {
  const { config, allDisabled, schema, setConfig } = props;
  const [built, setBuilt] = useState(cloneDeep(config));
  const onClickNext = useCallback(() => setConfig?.(built), [built, setConfig]);

  return (
    <Form layout="vertical" requiredMark="optional" preserve>
      {
        // By pushing the isFetching spinner to within the ConfigForm, we ensure
        // that we do not reset the form during fetches
        props.isFetching ? (
          <Spin />
        ) : (
          <>
            {isEmpty(schema) ? (
              <Spacing mb={12}>
                No configuration options exist for this item
              </Spacing>
            ) : (
              <ComponentForm
                {...props}
                config={built}
                setConfig={setBuilt}
                item={built}
              />
            )}
            {!allDisabled &&
              !(config.state === "installed" && isEmpty(schema)) && (
                <Form.Item key="submit">
                  {props.installer?.submitActions ? (
                    <Flex gap={8} align="center">
                      <SubmitButton
                        config={config}
                        disabled={isEmpty(built) && !isEmpty(schema)}
                        onClick={onClickNext}
                      />
                      <ConfigFormSubmitActions {...props} config={built} />
                    </Flex>
                  ) : (
                    <SubmitButton
                      config={config}
                      disabled={isEmpty(built) && !isEmpty(schema)}
                      onClick={onClickNext}
                    />
                  )}
                </Form.Item>
              )}
            <ConfigFormFooter {...props} config={built} />
          </>
        )
      }
    </Form>
  );
};
